/**
 * The stacked traces behind the `waveform` background effect.
 *
 * One row of the stack is two SVG paths over the same point list: a filled area
 * from the trace down to the baseline, and the open polyline of the trace
 * itself. They go into ONE mask, at two different alphas, which is what lets a
 * single layer per row show both a bright crest line and a faint body under it.
 * The alternative - a fill layer plus a stroke layer per row - doubles the DOM
 * and the injected CSS for no visible gain.
 *
 * Why this file exists at all (AGENTS.md §4): `screens.ts` holds hand-written
 * CSS chunks, and a point-generating loop is a different job with a different
 * failure mode. It also has to be TESTABLE without a DOM, because the one thing
 * that can silently ruin the effect is a point escaping the viewBox, where the
 * SVG would cut the trace off with a dead-flat edge.
 *
 * The shape is a fixed sum of three sines, NOT a random walk. The path ends up
 * inside a CSS string that is rendered on the server as well as in the browser,
 * so anything random would produce a different stylesheet on each side and a
 * hydration mismatch - the same reason `lightningPath.ts` uses a seeded LCG.
 * Three fixed harmonics per row, phase-shifted by the row index, gives every
 * row its own profile while staying identical on every render.
 */

/** Rows in the stack, back to front. */
export const WAVEFORM_ROWS = 8;

/** Samples along one trace. Enough that the fastest harmonic (4.3 cycles) still
 *  gets about ten points per period, so the polyline reads as a curve rather
 *  than as a chain of facets. */
const SAMPLES = 40;

const VIEW_W = 200;
const VIEW_H = 60;
/** Where a trace rests when its harmonics cancel. Peaks rise from here. */
const BASELINE = 44;
/** Peak height of a fully enveloped crest, in viewBox units. */
const AMPLITUDE = 29;

/** The trace of one row as `[x, y]` samples in viewBox units. */
export function waveformPoints(row: number): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i <= SAMPLES; i++) {
    const t = i / SAMPLES;
    const x = t * VIEW_W;
    // Centre-weighted envelope: a joy plot is busy in the middle and calm at
    // both ends, which is what stops the rows reading as eight equal ripples.
    const env = Math.pow(Math.sin(Math.PI * t), 1.6);
    const wave =
      0.55 * Math.sin(2 * Math.PI * 1.0 * t + row * 0.9) +
      0.3 * Math.sin(2 * Math.PI * 2.4 * t + row * 1.7 + 0.4) +
      0.15 * Math.sin(2 * Math.PI * 4.3 * t + row * 2.6 + 1.1);
    const y = BASELINE - env * AMPLITUDE * wave;
    // Clamp INSIDE the box rather than clamping the finished path: a point
    // beyond the viewBox is drawn with a flat cut, and a flat cut is the one
    // thing a trace never has.
    out.push([Number(x.toFixed(1)), Number(Math.min(VIEW_H - 1, Math.max(1, y)).toFixed(1))]);
  }
  return out;
}

/** The mask image for one row: faint filled body plus a bright crest line. */
export function waveformMask(row: number): string {
  const pts = waveformPoints(row);
  const line = pts.map(([x, y]) => `${x} ${y}`).join(" L");
  const area = `M0 ${VIEW_H} L${line} L${VIEW_W} ${VIEW_H} Z`;
  const svg =
    `%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${VIEW_W} ${VIEW_H}' preserveAspectRatio='none'%3E` +
    `%3Cpath d='${area}' fill='%23fff' fill-opacity='.17'/%3E` +
    `%3Cpath d='M${line}' fill='none' stroke='%23fff' stroke-width='1.2' stroke-linejoin='round' stroke-linecap='round'/%3E` +
    `%3C/svg%3E`;
  return `url("data:image/svg+xml,${svg}")`;
}
